/**
 * Data model for the Profit & Loss page's "Net Profit (monthly)" chart
 * (dashboards round 3, commit 6), shared by the entity scope, personal
 * scope, and the All Entities / All Personal aggregates -- one component,
 * fed from whichever canonical predicate that page/scope already uses:
 * get_entity_pnl (single entity), get_business_pnl (All Entities, scope=
 * 'entities'), or get_personal_pnl (personal / All Personal, scope=
 * 'personal' -- get_personal_pnl already sums every PERSONAL_ENTITY_IDS
 * member, so "personal scope" and "All Personal" are the same call). Never
 * a chart-side parallel filter over raw bookings/transactions.
 *
 * Each month's net figure is net_result_actual/net_result_forward
 * (services/entity_pnl.py) when the backend has provided that split, or the
 * unsplit net_result when it hasn't (an older fixture, or a source with no
 * split concept e.g. BR OS, whose query already excludes future dates so
 * its net_result is already fully actual). Same "actuals never include a
 * future-dated figure" rule the Gross Revenue chart already follows
 * (lib/entityGrossRevenueChart.js), just for the net-result field instead of
 * gross revenue: months before the current one are pure actuals; the
 * current month is actuals-to-date (net_result_actual) plus a stacked
 * forecast remainder (net_result_forward) when the backend reports one;
 * months after the current one are the SAME two-part shape when forward
 * data exists for them, or an explicit null gap (never a fabricated zero)
 * when it doesn't. Negative months are never Math.abs'd -- a stacked Bar
 * renders a negative value below the zero line natively.
 *
 * All Personal's P&L schema has no "revenue" field (services/entity_pnl.py
 * get_personal_pnl always routes through _portfolio_monthly_series, which
 * never emits a bare `revenue` key) -- irrelevant here, since this chart
 * only ever reads net_result/net_result_actual/net_result_forward, which
 * both source shapes provide.
 */

import { monthKeyToLabel, getActualsForecastBoundary } from './forecastChartUtils';
import { calendarYearMonthKeys } from './calendarYear';

function byMonthKey(list) {
  var m = {};
  (list || []).forEach(function (row) { if (row && row.month) m[row.month] = row; });
  return m;
}

/**
 * @param {object} input
 * @param {number} [input.year] - defaults to the current calendar year
 * @param {object[]} input.months - the canonical predicate's "months" array (get_entity_pnl / get_business_pnl / get_personal_pnl)
 * @param {Date} [input.refDate] - injectable "today" for the actuals/forecast boundary (tests)
 */
export function buildNetProfitChartData(input) {
  var year = input.year || new Date().getFullYear();
  var byMonth = byMonthKey(input.months);
  var boundary = getActualsForecastBoundary(input.refDate);

  var rows = [];
  calendarYearMonthKeys(year).forEach(function (mk) {
    var label = monthKeyToLabel(mk);
    var m = byMonth[mk];

    if (!m) {
      rows.push({ month: label, _monthKey: mk, net: null, net_forecast: null, _isForecast: false });
      return;
    }

    var hasSplit = m.net_result_actual != null || m.net_result_forward != null;
    // Both split fields are always computed together by the canonical
    // predicate (services/entity_pnl.py sets them on the same dict literal),
    // so hasSplit=true with net_result_actual specifically absent shouldn't
    // happen in practice -- but defaulting a missing actual to 0 there would
    // be exactly the banned silent-zero pattern (CLAUDE.md section 3), so it
    // stays a null gap rather than parseFloat(undefined) || 0.
    var actual = hasSplit
      ? (m.net_result_actual != null ? (parseFloat(m.net_result_actual) || 0) : null)
      : (parseFloat(m.net_result) || 0);
    var forward = hasSplit ? (parseFloat(m.net_result_forward) || 0) : 0;

    if (mk < boundary.currentMonthKey) {
      // Past: pure actuals, even if the backend also reported a (should-be-
      // zero) forward figure for it.
      rows.push({ month: label, _monthKey: mk, net: actual, net_forecast: null, _isForecast: false });
      return;
    }

    if (mk === boundary.currentMonthKey) {
      // Current month: actuals-to-date plus a labelled forecast remainder,
      // matching the Gross Revenue chart's current-month convention.
      rows.push({
        month: label, _monthKey: mk,
        net: actual,
        net_forecast: forward !== 0 ? forward : null,
        _isForecast: forward !== 0,
      });
      return;
    }

    // Future month: never an actual. Forecast-styled only when the backend
    // actually reports a split for it; otherwise an explicit null gap.
    if (!hasSplit) {
      rows.push({ month: label, _monthKey: mk, net: null, net_forecast: null, _isForecast: false });
      return;
    }
    rows.push({ month: label, _monthKey: mk, net: null, net_forecast: actual + forward, _isForecast: true });
  });

  return rows;
}

/**
 * Sum of the chart's ACTUAL-month values only (the current month's
 * actuals-to-date portion counts; its forecast remainder and every future
 * month do not) -- must equal the page's actuals-scoped YTD net figure, so
 * chart and stat card cannot disagree.
 */
export function computeNetProfitYtdActual(chartData) {
  return (chartData || []).reduce(function (sum, row) {
    return sum + (row.net || 0);
  }, 0);
}

/**
 * Net Profit stacked by source (BR OS / Property Portfolio), same
 * actuals/forecast rules as buildNetProfitChartData applied per source.
 * Each source's rows come from its own bros_* / portfolio_* fields
 * (services/entity_pnl._merged_bros_portfolio_pnl), which sum to the merged
 * net_result*. Row keys follow MarginChart: `<label>` (actual) and
 * `<label>_forecast`; `_total` is the month total (actual + forecast).
 */
export var NET_PROFIT_SOURCE_SEGMENTS = [
  { key: 'bros', label: 'BR OS', color: '#3B82F6' },
  { key: 'portfolio', label: 'Property Portfolio', color: '#10B981' },
];

// Country view (Fan, 2026-10-27): UK = BR OS + UK properties, France =
// French properties; uk_* + fr_* fields sum to the merged net_result*.
export var NET_PROFIT_COUNTRY_SEGMENTS = [
  // Flags always (Fan, 2026-10-27).
  { key: 'uk', label: '\uD83C\uDDEC\uD83C\uDDE7 UK', color: '#6366F1' },
  { key: 'fr', label: '\uD83C\uDDEB\uD83C\uDDF7 France', color: '#F59E0B' },
];

export function buildNetProfitBySourceChartData(input, segmentsArg) {
  var SEGMENTS = segmentsArg || NET_PROFIT_SOURCE_SEGMENTS;
  var perSource = SEGMENTS.map(function (seg) {
    var months = (input.months || []).map(function (m) {
      return {
        month: m.month,
        net_result: m[seg.key + '_net_result'],
        net_result_actual: m[seg.key + '_net_result_actual'],
        net_result_forward: m[seg.key + '_net_result_forward'],
      };
    });
    return buildNetProfitChartData({ year: input.year, months: months, refDate: input.refDate });
  });
  return perSource[0].map(function (base, i) {
    var row = { month: base.month, _monthKey: base._monthKey, _isForecast: false, _total: null };
    SEGMENTS.forEach(function (seg, s) {
      var r = perSource[s][i];
      row[seg.label] = r.net;
      row[seg.label + '_forecast'] = r.net_forecast;
      if (r._isForecast) row._isForecast = true;
      if (r.net != null || r.net_forecast != null) {
        row._total = (row._total || 0) + (r.net || 0) + (r.net_forecast || 0);
      }
    });
    return row;
  });
}
